// filepath: /Users/cm/Dev/aib/Frontend/photovortex/src/Admin/PluginOptions/PluginOptionValueEditModal.jsx
import {
  Modal,
  TextInput,
  Textarea,
  Button,
  Group,
  NumberInput,
  ActionIcon,
  Title,
  Box,
  Text,
  ScrollArea,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import {
  IconTrash,
  IconPlus,
  IconDeviceFloppy,
  IconExternalLink,
} from "@tabler/icons-react";
import { useEffect } from "react";

export default function PluginOptionValueEditModal({
  opened,
  onClose,
  valueData,
  onSave,
  isCreating,
  // pluginId, // Not directly used in this modal's internal logic but could be for API calls if made here
  // optionId, // Same as pluginId
}) {
  const form = useForm({
    mode: "uncontrolled",
    initialValues: {
      _internal_id: "", // Keep track of the internal ID for list management
      id: null, // Actual DB ID
      titleUser: "",
      prompt: "",
      previewImageUrl: "",
      civitai: "",
      // Future:
      loras: [],
    },
    validate: {
      titleUser: (value) =>
        value && value.trim().length >= 2
          ? null
          : "Title must have at least 2 characters",
      prompt: (value) =>
        value && value.trim().length >= 5
          ? null
          : "Prompt must have at least 5 characters",
      loras: {
        path: (value) => (value && value.trim() ? null : "Path is required"),
        scale: (value) =>
          value !== undefined && value !== null && value >= 0 && value <= 2
            ? null
            : "Scale must be between 0 and 2",
      },
    },
  });

  useEffect(() => {
    if (valueData) {
      console.debug("Modal received valueData:", valueData);
      form.setValues({
        _internal_id: valueData._internal_id || Date.now().toString(), // Ensure internal_id is always present
        id: valueData.id || null,
        titleUser: valueData.titleUser || "",
        prompt: valueData.prompt || "",
        previewImageUrl: valueData.previewImageUrl || "",
        lora: valueData.lora || "", // Single lora path
        defaultStrength: valueData.defaultStrength || 0.7, // Default strength for the single lora
        loras: valueData.lora
          ? [{ path: valueData.lora, scale: valueData.defaultStrength }]
          : [], // Initialize with lora if provided
        // loras: valueData.loras
        //   ? JSON.parse(JSON.stringify(valueData.loras))
        //   : [], // Deep copy loras
      });
    } else {
      // Reset form if no valueData (e.g. modal closed and reopened for new)
      form.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueData, opened]); // Rerun when valueData or opened status changes

  const handleFormSubmit = (values) => {
    console.debug("Modal form submitted with:", values);
    onSave(values); // Pass the edited/created data back to the parent
    // onClose(); // Parent component will handle closing
  };

  const loraFields = form.getValues().loras.map((item, index) => (
    <Group key={index} mt="xs" wrap="nowrap" align="flex-end">
      <TextInput
        label="LoRA Path/URL"
        placeholder="Enter LoRA path or URL"
        style={{ flex: 1 }}
        {...form.getInputProps(`loras.${index}.path`)}
        required
      />
      <NumberInput
        label="Scale"
        placeholder="0.0 to 2.0"
        min={0}
        max={2}
        step={0.01}
        precision={2}
        style={{ width: "100px" }}
        {...form.getInputProps(`loras.${index}.scale`)}
        required
      />
      <ActionIcon
        color="red"
        onClick={() => form.removeListItem("loras", index)}
        title="Remove LoRA"
      >
        <IconTrash size={18} />
      </ActionIcon>
    </Group>
  ));

  return (
    <Modal
      opened={opened}
      onClose={() => {
        form.reset(); // Reset form on close
        onClose();
      }}
      title={isCreating ? "Add New Option Value" : "Edit Option Value"}
      size="xl"
      centered
      scrollAreaComponent={ScrollArea.Autosize}
    >
      <form onSubmit={form.onSubmit(handleFormSubmit)}>
        <TextInput
          label="Title (User-Facing)"
          placeholder="E.g., Studio Background, Cinematic Lighting"
          {...form.getInputProps("titleUser")}
          required
          mb="md"
        />

        <Textarea
          label="Prompt Segment"
          placeholder="Enter the prompt segment for this value. This will be combined with other selections."
          {...form.getInputProps("prompt")}
          required
          autosize
          minRows={3}
          mb="md"
        />

        <TextInput
          label="Preview Image URL"
          placeholder="https://example.com/image.jpg (optional)"
          {...form.getInputProps("previewImageUrl")}
          mb="md"
        />
        {/* Future: Add FileInput for image upload here */}

        <TextInput label="LoRA URL" {...form.getInputProps("lora")} mb="md" />
        <NumberInput
          label="Default Strength"
          placeholder="0.0 to 2.0"
          min={0}
          max={2}
          step={0.01}
          precision={2}
          {...form.getInputProps("defaultStrength")}
          mb="md"
        />

        {valueData.civitai && (
          <a href={valueData.civitai} target="_blank">
            Civitai Link <IconExternalLink size={16} />
          </a>
        )}

        {/* <Box mb="md">
          <Group justify="space-between" mb="xs">
            <Text fw={500} size="sm">
              LoRAs
            </Text>
            <Button
              size="xs"
              variant="outline"
              leftSection={<IconPlus size={14} />}
              onClick={() =>
                form.insertListItem("loras", { path: "", scale: 0.7 })
              }
            >
              Add LoRA
            </Button>
          </Group>
          {loraFields.length > 0 ? (
            loraFields
          ) : (
            <Text c="dimmed" ta="center" size="sm" mt="sm">
              No LoRAs added for this value.
            </Text>
          )}
        </Box> */}

        <Group justify="flex-end" mt="xl">
          <Button
            variant="default"
            onClick={() => {
              form.reset();
              onClose();
            }}
          >
            Cancel
          </Button>
          <Button type="submit" leftSection={<IconDeviceFloppy size={18} />}>
            {isCreating ? "Add Value" : "Save Changes"}
          </Button>
        </Group>
      </form>
    </Modal>
  );
}
